import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, orderBy, Timestamp, deleteDoc } from 'firebase/firestore';
import { useRestaurant } from '../context/RestaurantContext';
import OrderCard from './OrderCard';
import TotalAmountModal from './TotalAmountModal';
import EditOrderModal from './EditOrderModal';
import { toast } from 'react-toastify';
import './Dashboard.css';

const Dashboard = () => {
  const { selectedRestaurant } = useRestaurant();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('Todas');
  const [loading, setLoading] = useState(true);
  const isInitialLoad = useRef(true);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState(null);

  const statusFilters = ['Todas', 'Pendiente', 'Preparando', 'Empaquetando', 'Proceso de Entrega', 'Entregado'];

  useEffect(() => {
    if (!selectedRestaurant) {
      setOrders([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);

    const q = query(
      collection(db, "orders"),
      where("restaurantId", "==", selectedRestaurant),
      where("timestamp", ">=", Timestamp.fromDate(startOfToday)),
      where("timestamp", "<=", Timestamp.fromDate(endOfToday)),
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);

      // Solo activa notificaciones después de la carga inicial
      if (!isInitialLoad.current) {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            const newOrder = change.doc.data();
            // La notificación de sonido ya no depende de sessionId
            toast.success(`¡Nueva Orden de ${newOrder.customer_name}!`);
            playNotificationSound();
          }
        });
      }
      isInitialLoad.current = false;
    }, (error) => {
      console.error("Error en la suscripción de Firestore:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [selectedRestaurant]);

  const playNotificationSound = () => {
    const audio = new Audio('/sound_new_notification.mp3');
    audio.play().catch(e => console.error("Error al reproducir sonido:", e));
  };
  
  const handleStatusChange = async (orderId, newStatus) => {
    if (newStatus === 'Empaquetando') {
      const orderToEdit = orders.find(order => order.id === orderId);
      setCurrentOrder(orderToEdit);
      setIsModalOpen(true);
    } else {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, { status: newStatus });
    }
  };

  const handleSaveTotal = async (orderId, totalAmount) => {
    const orderRef = doc(db, 'orders', orderId);
    try {
      await updateDoc(orderRef, {
        total: totalAmount,
        status: 'Empaquetando'
      });
      setIsModalOpen(false);
      setCurrentOrder(null);
    } catch (error) {
      console.error("Error al guardar el total: ", error);
      alert("Hubo un error al guardar el total de la orden.");
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm(`¿Estás seguro de que quieres borrar la orden?`)) {
      const orderRef = doc(db, 'orders', orderId);
      try {
        await deleteDoc(orderRef);
        toast.error("Orden borrada exitosamente.");
      } catch (error) {
        console.error("Error al borrar la orden: ", error);
      }
    }
  };
  
  const handleOpenEditModal = (order) => {
    setOrderToEdit(order);
    setIsEditModalOpen(true);
  };

  const handleUpdateOrder = async (orderId, updatedData) => {
    const orderRef = doc(db, 'orders', orderId);
    try {
      await updateDoc(orderRef, updatedData);
      toast.success("¡Orden actualizada con éxito!");
      setIsEditModalOpen(false);
      setOrderToEdit(null);
    } catch (error) {
      console.error("Error al actualizar la orden: ", error);
      toast.error("Hubo un error al actualizar la orden.");
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'Todas') return true;
    return order.status === filter;
  });
  
  const formatRestaurantName = (id) => {
      if (!id) return '';
      return id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="dashboard-container">
      {isModalOpen && currentOrder && (
        <TotalAmountModal order={currentOrder} onSave={handleSaveTotal} onClose={() => setIsModalOpen(false)} />
      )}
      {isEditModalOpen && orderToEdit && (
        <EditOrderModal order={orderToEdit} onSave={handleUpdateOrder} onClose={() => setIsEditModalOpen(false)} />
      )}
      
      <div className="filter-bar">
        {statusFilters.map(status => (
          <button 
            key={status}
            className={`filter-button ${filter === status ? 'active' : ''}`}
            onClick={() => setFilter(status)}
          >
            {status}
          </button>
        ))}
      </div>
      
      {loading ? <p>Cargando órdenes para {formatRestaurantName(selectedRestaurant)}...</p> : (
        <div className="orders-grid">
          {filteredOrders.length > 0 ? (
            filteredOrders.map(order => (
              <OrderCard 
                key={order.id} 
                order={order} 
                onStatusChange={handleStatusChange}
                onDelete={handleDeleteOrder}
                onStatusChangeSound={playNotificationSound}
                onOpenEditModal={handleOpenEditModal}
              />
            ))
          ) : <p>No hay órdenes para mostrar en este restaurante.</p>}
        </div>
      )}
    </div>
  );
};

export default Dashboard;